import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/i18n/useTranslation";
import { Board, Project, Sprint, BoardData } from "./boardTypes";
import { DEFAULT_COLUMNS, DEFAULT_COLUMN_ORDER } from "./boardConstants";
import { TaskRole } from "@/components/scheduler/schedulerTypes";
import { supabase } from "@/integrations/supabase/client";
import * as boardService from "./boardService";
import { useUser } from "@/contexts/UserContext";

export const useBoard = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { companyId } = useUser(); // Get company ID directly from useUser
  
  const [boardData, setBoardData] = useState<BoardData>({
    tasks: {},
    columns: DEFAULT_COLUMNS,
    columnOrder: DEFAULT_COLUMN_ORDER,
  });

  const [selectedRole, setSelectedRole] = useState<TaskRole | 'all'>('all');
  const [selectedSprintId, setSelectedSprintId] = useState<string | 'all'>('all');
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedSprint, setSelectedSprint] = useState<Sprint | null>(null);
  const [openBoardSelector, setOpenBoardSelector] = useState(false);
  
  useEffect(() => {
    const boardIdFromUrl = searchParams.get('boardId');
    if (boardIdFromUrl) {
      setSelectedBoardId(boardIdFromUrl);
    }
    
    const channel = supabase
      .channel('tasks-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'tasks' 
      }, () => {
        fetchTasks();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [searchParams]);
  
  useEffect(() => {
    if (companyId) {
      fetchBoards();
    }
  }, [companyId]);
  
  useEffect(() => {
    if (selectedBoardId) {
      fetchTasks();
      fetchCurrentSprint();
      fetchProjectDetails();
    }
  }, [selectedSprintId, selectedBoardId]);
  
  const fetchBoards = async () => {
    try {
      const { boards, projects } = await boardService.fetchBoards(companyId);
      setBoards(boards);
      
      const boardIdFromUrl = searchParams.get('boardId');
      if (boardIdFromUrl && boards.some(board => board.id === boardIdFromUrl)) {
        setSelectedBoardId(boardIdFromUrl);
        await boardService.ensureBoardHasBacklog(boardIdFromUrl);
      } else if (!selectedBoardId && boards.length > 0) {
        setSelectedBoardId(boards[0].id);
        await boardService.ensureBoardHasBacklog(boards[0].id);
      } else if (projects.length > 0 && boards.length === 0) {
        // Create default board if no boards exist
        const newBoard = await boardService.createDefaultBoard(projects[0].id);
        if (newBoard) {
          setBoards([newBoard]);
          setSelectedBoardId(newBoard.id);
          await boardService.createBacklogSprint(newBoard.id);
        }
      }
    } catch (error) {
      console.error('Error fetching boards:', error);
      toast({
        title: t('common.error'),
        description: t('board.errorFetchingBoards'),
        variant: "destructive",
      });
    }
  };
  
  const fetchProjectDetails = async () => {
    try {
      const project = await boardService.fetchProjectDetails(selectedBoardId);
      setSelectedProject(project);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };
  
  const fetchCurrentSprint = async () => {
    if (selectedSprintId === 'all') {
      setSelectedSprint(null);
      return;
    }
    
    try {
      const sprint = await boardService.fetchSprint(selectedSprintId);
      setSelectedSprint(sprint);
    } catch (error) {
      console.error('Error fetching sprint details:', error);
      setSelectedSprint(null);
    }
  };
  
  const fetchTasks = async () => {
    if (!selectedBoardId) return;
    
    setLoading(true);
    try {
      const boardData = await boardService.fetchTasks(selectedBoardId, selectedSprintId);
      setBoardData(boardData);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast({
        title: t('common.error'),
        description: t('board.errorFetchingTasks'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateTask = async (title: string, description: string, role: TaskRole, sprintId: string, employeeId?: number) => {
    try {
      await boardService.createTask(title, description, role, sprintId, employeeId);
      toast({
        title: t('board.taskCreated'),
        description: t('board.taskAddedSuccessfully'),
      });
      fetchTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast({
        title: t('common.error'),
        description: t('board.errorCreatingTask'),
        variant: "destructive",
      });
    }
  };
  
  const handleUpdateTask = async (taskId: string, title: string, description: string, role: TaskRole, sprintId: string, employeeId?: number) => {
    try {
      await boardService.updateTask(taskId, title, description, role, sprintId, employeeId);
      toast({
        title: t('board.taskUpdated'),
        description: t('board.taskUpdatedSuccessfully'),
      });
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      toast({
        title: t('common.error'),
        description: t('board.errorUpdatingTask'),
        variant: "destructive",
      });
    }
  };
  
  const handleCreateSprint = async (title: string, startDate: Date, endDate: Date, status: string, boardId: string | null) => {
    try {
      const newSprint = await boardService.createSprint(title, startDate, endDate, status, boardId);
      toast({
        title: t('board.sprintCreated'),
        description: t('board.sprintAddedSuccessfully'),
      });
      
      if (newSprint) {
        setSelectedSprintId(newSprint.id);
      }
    } catch (error) {
      console.error("Error creating sprint:", error);
      toast({
        title: t('common.error'),
        description: t('board.errorCreatingSprint'),
        variant: "destructive",
      });
    }
  };
  
  return {
    boardData,
    setBoardData,
    selectedRole,
    setSelectedRole,
    selectedSprintId,
    setSelectedSprintId,
    selectedBoardId,
    setSelectedBoardId,
    sidebarExpanded,
    setSidebarExpanded,
    loading,
    boards,
    selectedProject,
    selectedSprint,
    openBoardSelector,
    setOpenBoardSelector,
    handleCreateTask,
    handleUpdateTask,
    handleCreateSprint,
    fetchTasks
  };
};